<template>
  <section>
    <v-card class="pa-4 mb-4">
      <!-- header filters -->
      <v-row>
        <v-col>
          <h5 class="grey--text">
            {{ t.filterByAnalysisStatus }}
          </h5>
        </v-col>
      </v-row>

      <v-row style="flex-wrap: nowrap;">
        <v-col class="flex-grow-1 pl-4">
          <v-checkbox
            v-for="option of analysisOptions"
            :key="option.key"
            v-model="option.val"
            :label="`${option.name} (${occurences[option.key] || 0})`"
            hide-details
            class="mr-5 d-inline-block"
          />
        </v-col>
        <v-col class="flex-grow-1">
          <v-text-field
            v-model="searchTerm"
            :label="t.filterByTitleOfSummary"
            outlined
            class="mr-5"
          />
        </v-col>
        <v-col class="flex-grow-0">
          <v-btn
            color="primary"
            class="mt-3"
            @click="resetFilters"
          >
            {{ t.resetFilters }}
          </v-btn>
        </v-col>
      </v-row>

      <v-spacer class="py-5" />

      <v-row>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            text
            @click="onExpandToggle"
          >
            {{ expandButtonText }}
            <v-icon
              right
              dark
            >
              unfold_more
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-expansion-panels
      v-model="expandedPanels"
      multiple
    >
      <v-expansion-panel
        v-for="category of Object.keys(longTermOptions)"
        :key="category"
      >
        <template v-if="shouldShowCategory(category)">
          <v-expansion-panel-header>
            <h4 class="d-inline-block">
              {{ category }}
            </h4>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-row>
              <v-spacer />
              <v-col cols="auto">
                <v-btn
                  text
                  @click="onExpandCategoryToggle(category)"
                >
                  {{ expandCategoryButtonText(category) }}
                  <v-icon
                    right
                    dark
                  >
                    unfold_more
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-expansion-panels
              v-model="expandedSubPanels[category]"
              multiple
            >
              <v-expansion-panel
                v-for="(section, index) of longTermOptions[category]"
                :key="`${category}-${index}`"
              >
                <template v-if="shouldShowByStatus(section)">
                  <v-expansion-panel-header>
                    <v-row no-gutters>
                      <v-col cols="2">
                        <h5 class="d-inline-block">
                          {{ section.legalReference }}
                        </h5>
                        <a
                          v-if="section.legalReference && section.link"
                          :href="section.link"
                          target="_new"
                        >
                          <v-icon small>mdi-arrow-top-right</v-icon>
                        </a>
                      </v-col>
                      <v-col>
                        <v-row>
                          <v-col class="d-flex justify-space-between">
                            <h4>{{ section.shortTitle }}</h4>

                            <h5
                              class="rounded-lg py-2 px-4 mr-4 white--text"
                              :class="[getSectionStatus(section).color]"
                            >
                              {{ getSectionStatus(section).name }}
                            </h5>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col>
                            <p class="text-body-2">
                              {{ section.summary }}
                            </p>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="8">
                        <v-textarea
                          v-model="section.narrative"
                          auto-grow
                          outlined
                          :label="t.narrative"
                          :disabled="section.locked || !peUserCanUpdate"
                        />
                      </v-col>
                      <v-col>
                        <v-select
                          v-model="section.status"
                          outlined
                          :items="analysisOptions"
                          item-text="name"
                          item-value="key"
                          :label="t.analysisStatus"
                          :disabled="section.locked || !peUserCanUpdate"
                        />

                        <template v-if="peUserCanUpdate">
                          <template v-if="section.locked">
                            <v-btn
                              depressed
                              color="primary"
                              class="float-right"
                              @click="switchEditing(section)"
                            >
                              {{ t.edit }}
                              <v-icon
                                small
                                class="ml-2"
                              >
                                mdi-pencil
                              </v-icon>
                            </v-btn>
                          </template>

                          <template v-if="!section.locked">
                            <v-btn
                              depressed
                              color="primary"
                              class="float-right"
                              @click="onSaveSection(section)"
                            >
                              {{ t.lock }}
                              <v-icon
                                small
                                class="ml-2"
                              >
                                mdi-lock
                              </v-icon>
                            </v-btn>
                          </template>
                        </template>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </template>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </template>
      </v-expansion-panel>
    </v-expansion-panels>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { flatten } from 'lodash'

import { alertHandler } from '@/alertHandler'

import { LONG_TERM_OPTIONS_NAMESPACE } from '@/store/long-term-options'
import { PARTY_NAMESPACE } from '@/store/party'

export default {
  name: 'LongTermOptionsTab',

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      expandedPanels: [],
      expandedSubPanels: {},
      infoPanel: {
        eligibility: '',
        ineligibility: '',
        show: false,
        title: '',
      },

      partyEligibilities: [],
      searchTerm: '',
      t: {
        analysisStatus: 'Analysis Status',
        collapseAll: 'Collapse All',
        edit: 'Edit',
        expandAll: 'Expand All',
        filterByAnalysisStatus: 'Filter by Analysis Status',
        filterByTitleOfSummary: 'Filter by Title or Summary',
        lock: 'Save',
        narrative: 'Narrative',
        resetFilters: 'Reset',
        shortTitle: 'Short Title',
        statutoryCitation: 'Statutory Citation',
      },
    }
  },

  computed: {
    expandButtonText () {
      return (this.shouldExpandCategories ? this.t.expandAll : this.t.collapseAll) + ' Categories'
    },

    occurences () {
      return flatten(Object.values(this.longTermOptions)).reduce((p, c) => {
        const status = c.status || 'no-analysis'
        if (!p[status]) p[status] = 0
        p[status]++
        return p
      }, {})
    },

    partyId () { return this.id },

    shouldExpandCategories () {
      return this.expandedPanels.length < 2
    },

    statusFilter () {
      return this.analysisOptions.reduce((p, c) => {
        p[c.key] = c.val || false
        return p
      }, {})
    },

    ...mapGetters(LONG_TERM_OPTIONS_NAMESPACE, [
      'analysisOptions',
      'longTermOptions',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peUserCanUpdate',
    ]),
  },

  async created () {
    await this.peGetPartySection({ id: this.partyId }).
      catch(_ => this.displayAlert('error', `No Party found with the id: ${this.id}`))
    await this.getLongTermOptions({ partyId: this.partyId }).
      then(_ => this.updateSubPanels())
  },

  methods: {
    collapseAll () {
      this.expandedPanels = []
    },

    expandAll () {
      this.expandedPanels = this.longTermOptions.map((k, i) => i)
    },

    expandCategoryButtonText (category) {
      return (this.shouldExpandCategoryItems(category) ? this.t.expandAll : this.t.collapseAll) + ` ${category}`
    },

    getSectionStatus (section) {
      const status = section.status || 'no-analysis'
      return this.analysisOptions.find(option => option.key === status)
    },

    onExpandCategoryToggle (category) {
      if (this.shouldExpandCategoryItems(category)) {
        this.expandedSubPanels[category] = [...Object.keys(this.longTermOptions[category]).keys()]
      } else this.expandedSubPanels[category] = []
    },

    onExpandToggle () {
      if (this.shouldExpandCategories) this.expandedPanels = [...Object.keys(this.longTermOptions).keys()]
      else this.expandedPanels = []
    },

    onSaveSection (section) {
      section.locked = true
      this.saveLongTermOption({ partyId: this.partyId, section }).
        then(() => this.displayAlert('success', 'Long Term Option saved successfully')).
        catch(() => this.displayAlert('error', 'error saving Long term Option'))
    },

    resetFilters () {
      this.searchTerm = ''
      for (const option of this.analysisOptions) option.val = false
    },

    shouldExpandCategoryItems (category) {
      return this.expandedSubPanels[category]?.length < 2
    },

    shouldShowBySearchTerm (section) {
      const sectionHash = Object.values(section).join()
      return sectionHash.includes(this.searchTerm)
    },

    shouldShowByStatus (section) {
      if (!this.analysisOptions.find(o => o.val)) return this.shouldShowBySearchTerm(section)
      return this.statusFilter[section.status || 'no-analysis'] && this.shouldShowBySearchTerm(section)
    },

    shouldShowCategory (category) {
      return this.longTermOptions[category]?.find(o => this.shouldShowByStatus(o))
    },

    showInfo (section) {
      this.infoPanel.title = section.short_title
      this.infoPanel.eligibility = section.eligibility
      this.infoPanel.ineligibility = section.ineligibility

      this.infoPanel.show = true
    },

    switchEditing (section) {
      section.locked = false
      this.saveLongTermOption({ partyId: this.partyId, section }).
        then(() => this.displayAlert('success', 'Long Term Option unlocked successfully')).
        catch(() => this.displayAlert('error', 'error unlocking Long term Option'))
    },

    updateSubPanels () {
      this.expandedSubPanels = Object.keys(this.longTermOptions).
        reduce((p, c) => {
          p[c] = []
          return p
        }, {})
    },

    ...mapActions(LONG_TERM_OPTIONS_NAMESPACE, [
      'getLongTermOptions',
      'saveLongTermOption',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'peGetPartySection',
    ]),
  },
}
</script>
